<script setup lang="ts">
import { computed } from 'vue';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import { V1SnapshotStrictness } from '@factoryfixinc/ats-interfaces';
import ExpandableSection from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/ExpandableSection/ExpandableSection.vue';
import MultipleSelectionItems from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/MultipleSelectionItems/MultipleSelectionItems.vue';
import InsightfulTextArea from './InsightfulTextArea/InsightfulTextArea.vue';

const copilotActivationService = new CopilotActivationService();

const selectedStrictness = computed(() => {
  return copilotActivationService.projectCopilotConfigurationV1Strictness;
});

const selectedStrictnessItems = computed(() => {
  return [
    {
      title: 'Lenient',
      subtitle: 'Consider candidates with transferable skills and potential to learn',
      icon: 'expand' as const,
      isSelected: selectedStrictness.value === V1SnapshotStrictness.LENIENT,
      tooltipText: '',
      value: `${V1SnapshotStrictness.LENIENT}`,
    },
    {
      title: 'Balanced',
      subtitle: 'Balance technical requirements with demonstrated mechanical aptitude',
      icon: 'scale' as const,
      isSelected: selectedStrictness.value === V1SnapshotStrictness.BALANCED,
      tooltipText: '',
      value: `${V1SnapshotStrictness.BALANCED}`,
    },
    {
      title: 'Strict',
      subtitle: 'Focus on candidates with exact experience and certifications needed',
      icon: 'target' as const,
      isSelected: selectedStrictness.value === V1SnapshotStrictness.STRICT,
      tooltipText: '',
      value: `${V1SnapshotStrictness.STRICT}`,
    },
  ];
});

const handleStrictnessSelection = (value: string) => {
  copilotActivationService.projectCopilotConfigurationV1Strictness = value;
};
</script>

<template>
  <ExpandableSection title="Help your AI recruiter understand your ideal candidate (Optional)">
    <MultipleSelectionItems
      title="How strictly should your AI recruiter evaluate candidates?"
      :items="selectedStrictnessItems"
      class="mb-8"
      @click:select="handleStrictnessSelection"
    />
    <InsightfulTextArea
      v-model="copilotActivationService.projectCopilotConfigurationV1RelevantSkills"
      v-model:ai-insight="copilotActivationService.aiLearningsRelevantSkills"
      title="What experience level or skills separate an okay candidate from a great one for this position?"
      :placeholder="`e.g. experience optimizing production processes, ability to troubleshoot complex mechanical systems, cross-trained in multiple areas of manufacturing...`"
    />

    <InsightfulTextArea
      v-model="copilotActivationService.projectCopilotConfigurationV1RelevantBackground"
      v-model:ai-insight="copilotActivationService.aiLearningsRelevantBackground"
      title="What aspects of someone's background make you willing to overlook other shortcomings?"
      :placeholder="`e.g. extensive experience with similar manufacturing equipment, demonstrated ability to learn new systems quickly, strong mechanical aptitude with transferable skills...`"
      class="mt-6"
    />

    <InsightfulTextArea
      v-model="copilotActivationService.projectCopilotConfigurationV1ImmediateRejectionReason"
      v-model:ai-insight="copilotActivationService.aiLearningsImmediateRejectionReason"
      title="What signals would tell you a candidate isn't the right fit?"
      :placeholder="`e.g. no experience with required machinery, lack of safety certifications, gaps in critical technical skills...`"
      class="mt-6"
    />
    <p class="helper-text">
      Used to guide scoring + screening. We won't auto-reject candidates for you.
    </p>
  </ExpandableSection>
</template>

<style lang="scss" scoped>
.helper-text {
  @apply text-xs font-normal text-shade-800;
}
</style>
